import { useState, useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import eventBus, { eventBusValues } from './../../eventBus';
import { ReactComponent as PaperPlusIcon } from './../../icons/paper-plus.svg';
import { ReactComponent as CalendarCheckIcon } from './../../icons/calendar-check.svg';
import { ReactComponent as BooksIcon } from './../../icons/books.svg';
import { getCompanyBySlug } from '../../services/company_services';
import { useAccount } from './../../contexts/Account';
import BoardroomHeader from './BoardroomHeader';
import BenefitTiles from './BenefitTiles';
import CurrentPriorities from './CurrentPriorities';
import Activities from './Activities';
import FirstViewModal from './FirstViewModal';
import { addBoardView } from '../../services/advisor_services';
import LoadingIndicatorLogo from '../../components/LoadingIndicatorLogo';
import { getAdvisorQueryParams } from './../../utils/queryParams';
import UpsellDialog from './UpsellDialog';
import Footer from './Footer';
import BenefitsAndPerks from './BenefitsAndPerks';
import BoardTile from '../../components/BoardTile';
import BoardroomActivity from '../../components/BoardroomActivity';
import Treatment from '../../components/Treatment';
import { featureFlags } from '../../splitSetup';
import LatestBoardUpdate from './LatestBoardUpdate';

const tileTitleStyle = {
  fontSize: { xxs: 16, xs: 20 },
  lineHeight: { xxs: '20px', xs: '26px' },
  fontWeight: '600',
  marginBottom: { xxs: '7px', xs: '14px' },
  textAlign: 'left',
};

const tileDescriptionStyle = {
  fontSize: 12,
  lineHeight: '18px',
  fontWeight: '400',
  color: '#4D586F',
  textAlign: 'left',
};

export default function Boardroom() {
  const postContainerRef = useRef(null);
  const params = useParams('/:companySlug/');
  const { companySlug } = params;
  const [company, setCompany] = useState({});
  const [showBoardMeeting, setShowBoardMeeting] = useState(false);
  const [showPerksAndBenefits, setShowPerksAndBenefits] = useState(false);
  const { accountDetails, currentPlan, isFreeTrial, isFreemium } = useAccount();
  const { specialOffersUrl } = currentPlan;
  const finalUrl = `${specialOffersUrl}?${getAdvisorQueryParams({
    accountDetails,
    isFreeTrial,
    isFreemium,
  })}`;
  const [isFirstView, setIsFirstView] = useState(false);
  const [isFirstViewModalVisible, setIsFirstViewModalVisible] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const memoizedCompany = useMemo(() => company, [company]);
  const memoizedPostContainerRef = useRef(postContainerRef);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (!company.id || !accountDetails) return;
    const isJoined = company?.boardAdvisors?.some(
      (boardAdvisor) => boardAdvisor.id === accountDetails.id
    );
    const justJoined = location?.state?.justJoined
      ? location?.state?.companyId == company.id
      : false;

    if (!isJoined && !justJoined) {
      history.push({
        pathname: '/board-directory',
        state: { companyId: company.id },
      });
    }
  }, [company]);

  useEffect(async () => {
    try {
      const { data } = await getCompanyBySlug({
        slug: companySlug,
        getBreakthroughs: true,
      });
      if (data && data.getCompany) {
        setInitialLoad(false);
        setCompany(data.getCompany);
      }
    } catch (error) {
      // TODO: Handle error
      console.error({ error });
      setInitialLoad(false);
    }
  }, []);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  //useEffect to check if the board has been viewed before
  useEffect(() => {
    if (!company.id || !accountDetails) return;
    if (
      !accountDetails.viewedBoards ||
      (accountDetails.viewedBoards &&
        !accountDetails.viewedBoards.includes(company.id))
    ) {
      setIsFirstView(true);
      setIsFirstViewModalVisible(true);
    }

    if (isFirstView) {
      try {
        addBoardView({
          ADVISOR_ID: accountDetails.id,
          COMPANY_ID: company.id,
        });
        accountDetails.viewedBoards.push(company.id);
      } catch (error) {
        console.error('Error: ', error);
      }
    }
  }, [company, isFirstView]);

  function seeOpportunity(companyId) {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      companyId,
    });
  }

  if (initialLoad) {
    return (
      <div
        style={{
          alignItems: 'center',
          backgroundColor: '#FFF',
          bottom: '0px',
          display: 'flex',
          justifyContent: 'center',
          left: '0px',
          position: 'fixed',
          right: '0px',
          top: '0px',
        }}
      >
        <LoadingIndicatorLogo size={200} iconFontSize={93} iconRight={105} />
      </div>
    );
  }

  return (
    <Box sx={{ p: { xxs: 2, xs: 4 } }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <BoardroomHeader
            company={company}
            setIsLoading={setIsLoading}
            seeOpportunity={seeOpportunity}
            setIsFirstViewModalVisible={setIsFirstViewModalVisible}
          />
        </Grid>
        <Grid item xxs={12} md={3}>
          {company.benefits && company.benefits.length > 0 && (
            <BenefitTiles benefits={company.benefits} />
          )}
        </Grid>
      </Grid>
      <LatestBoardUpdate companyId={company.id} />
      <Grid container spacing={2} sx={{ mt: 0, mb: 2 }}>
        <Grid item xs={12} md={6}>
          <CurrentPriorities
            company={company}
            seeOpportunity={seeOpportunity}
          />
        </Grid>
        <Treatment
          splitNames={featureFlags.breakthroughs}
          evaluatedComponent={
            <Grid item xs={12} md={6}>
              <BoardroomActivity
                company={company}
                postContainerRef={postContainerRef}
                seeOpportunity={seeOpportunity}
                breakthroughsEnabled
              />
            </Grid>
          }
          conditionFailedComponent={
            <Grid item xs={12} md={6}>
              <BoardroomActivity
                company={company}
                postContainerRef={postContainerRef}
                seeOpportunity={seeOpportunity}
              />
            </Grid>
          }
        />
      </Grid>
      <Grid container spacing={2}>
        <BoardTile
          image={<CalendarCheckIcon style={{ width: 60, height: 60 }} />}
          onClick={() => history.push(`/ub/other-ways-to-get-involved`)}
        >
          <Typography sx={tileTitleStyle}>Ways to Get Involved</Typography>
          <Typography sx={tileDescriptionStyle} ref={postContainerRef}>
            Explore other ways to get involved on the advisory board: make an
            introduction, provide a testimonial or attend a board meeting.
          </Typography>
        </BoardTile>

        <BoardTile
          image={<BooksIcon style={{ width: 90, height: 90 }} />}
          onClick={() => seeOpportunity(company?.id)}
        >
          <Typography sx={tileTitleStyle}>Background Materials</Typography>
          <Typography sx={tileDescriptionStyle}>
            Learn more about this advisory board and its leadership, plus access
            key documents and confidential board materials.
          </Typography>
        </BoardTile>

        <BoardTile
          image={<CalendarCheckIcon style={{ width: 60, height: 60 }} />}
          onClick={() => {
            setShowBoardMeeting((prev) => !prev);
            setShowPerksAndBenefits(false);
          }}
        >
          <Typography sx={tileTitleStyle}>Boardroom Activity</Typography>
          <Typography sx={tileDescriptionStyle}>
            View the full log of boardroom activity, advisory board
            announcements and favorite ideas shared in the boardroom.
          </Typography>
        </BoardTile>

        <BoardTile
          image={<PaperPlusIcon style={{ width: 60, height: 60 }} />}
          onClick={() => {
            setShowPerksAndBenefits((prev) => !prev);
            setShowBoardMeeting(false);
          }}
        >
          <Typography sx={tileTitleStyle}>Benefits & Perks</Typography>
          <Typography sx={tileDescriptionStyle}>
            Check out the exciting benefits and perks being offered to motivated
            and helpful members of advisory board.
          </Typography>
        </BoardTile>
      </Grid>

      {showBoardMeeting && company && company.id && (
        <Activities
          company={memoizedCompany}
          postContainerRef={memoizedPostContainerRef.current}
          scrollOnFirstLoad
        />
      )}
      {showPerksAndBenefits && company && company.id && (
        <BenefitsAndPerks
          company={memoizedCompany}
          postContainerRef={memoizedPostContainerRef.current}
          showPerksAndBenefits={showPerksAndBenefits}
        />
      )}
      <Footer company={company} />
      <UpsellDialog
        open={openDialog}
        onClose={handleCloseDialog}
        finalUrl={finalUrl}
      />
      {company && company.id ? (
        <FirstViewModal
          companyBoardData={company}
          isFirstViewModalVisible={isFirstViewModalVisible}
          setIsFirstViewModalVisible={setIsFirstViewModalVisible}
        />
      ) : null}
      {isLoading && (
        <Box
          sx={{
            alignItems: 'center',
            bottom: '0px',
            display: 'flex',
            justifyContent: 'center',
            left: '0px',
            position: 'fixed',
            right: '0px',
            top: '0px',
            zIndex: 9999,
          }}
        >
          <LoadingIndicatorLogo size={200} iconFontSize={93} iconRight={105} />
        </Box>
      )}
    </Box>
  );
}
